import { useNavigate } from "react-router-dom";
import Navbar from "../../components/tickets/navBar";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import Footer from '../../components/tickets/footer';

const sections = [
  {
    number: "1",
    title: "LEGAL ENTITY STRUCTURE, OWNERSHIP AND ACCEPTANCE",
    content: `This Agreement governs the access to and use of the Afrodise Music platform (the "Platform").

The Platform is operated by Afrodise Music SRL, a company duly incorporated under the laws of Romania, having its registered office in Bucharest, Romania, registered under CIF RO52584816 (hereinafter referred to as the "Operator").

The Platform, including its brand, intellectual property, and global event ecosystem, is owned and developed by Afrodise Music, an entity operating from the Ras Al Khaimah Economic Zone, United Arab Emirates, with a registered business address at:

CO-Working Facility FDCW1137, Compass Building, Al Shohada Road, Al Hamra Industrial Zone-FZ, Ras Al Khaimah, United Arab Emirates (hereinafter referred to as the "Brand Owner").

Afrodise Music SRL and Afrodise Music (UAE) are separate and independent legal entities, each operating under distinct jurisdictions and legal frameworks.

By accessing, browsing, registering on, or purchasing through the Platform, the user (the "User") acknowledges that they have read, understood, and fully accepted this Agreement and agree to be legally bound by its provisions. Such acceptance constitutes a legally binding electronic agreement equivalent to a handwritten signature.`,
  },
  {
    number: "2",
    title: "NATURE OF THE PLATFORM AND ROLE OF THE PARTIES",
    content: `The Platform functions as a digital infrastructure facilitating ticket sales, event promotion, and electronic delivery of access credentials.

In most cases, Afrodise Music SRL acts solely as an intermediary providing technological and transactional services that enable Users to purchase tickets from third-party event organizers.

In certain cases, where explicitly indicated on the event page, Afrodise Music (UAE) or Afrodise Music SRL may act as the event organizer. The identity of the organizer is always displayed on the event page and determines the allocation of legal responsibility.

The User expressly acknowledges that:
• The Platform Operator is not responsible for events organized by third parties.
• Obligations relating to event execution rest exclusively with the identified organizer.`,
  },
  {
    number: "3",
    title: "MERCHANT OF RECORD AND PAYMENT PROCESSING",
    content: `Afrodise Music SRL acts as the exclusive Merchant of Record for all transactions conducted through the Platform.

All payments are processed through payment service providers, including but not limited to Stripe and PayPal, under accounts owned and controlled by Afrodise Music SRL.

As Merchant of Record:
• Afrodise Music SRL is responsible for processing payments, issuing transaction confirmations, and handling payment-related disputes.
• The User's payment obligation is fulfilled upon successful payment to Afrodise Music SRL.
• Afrodise Music SRL collects funds on behalf of the event organizer, where applicable.

All transactions are subject to fraud detection systems, identity verification procedures, and risk and compliance screening.

The Platform reserves the right to refuse or cancel transactions deemed suspicious, non-compliant, or fraudulent.

Users agree not to initiate unjustified chargebacks. Any abusive chargeback activity may result in account suspension, recovery proceedings, and legal action.`,
  },
  {
    number: "4",
    title: "TAXATION AND VAT TREATMENT",
    content: `All payments processed through the Platform are handled by Afrodise Music SRL as Merchant of Record, and therefore:
• Applicable taxes, including Value Added Tax (VAT), are calculated and applied in accordance with Romanian and European Union legislation.
• Afrodise Music SRL may issue invoices or receipts in compliance with applicable fiscal regulations.
• Where required, tax obligations related to event organization remain the responsibility of the event organizer.

The User acknowledges that:
• Ticket prices may include taxes, service fees, and processing costs.
• Cross-border transactions may be subject to additional regulatory requirements.`,
  },
  {
    number: "5",
    title: "CONTRACTUAL RELATIONSHIP",
    content: `Each ticket purchase constitutes a legally binding distance contract.

Where Afrodise Music SRL acts solely as intermediary, the contract exists between the User and the event organizer.

Where Afrodise Music SRL or Afrodise Music (UAE) is identified as organizer, the contract is concluded directly with that entity.

The Platform shall not be considered a party to event-related contracts unless explicitly stated.`,
  },
  {
    number: "6",
    title: "USER ACCOUNTS AND SECURITY",
    content: `Users are required to maintain accurate account information and safeguard their login credentials.

The User assumes full responsibility for:
• All activities conducted under their account.
• Protection of digital tickets and access credentials.

The Platform shall not be liable for unauthorized access, misuse, or ticket duplication.`,
  },
  {
    number: "7",
    title: "PAYMENT TERMS AND FRAUD PREVENTION",
    content: `Payments must be completed in full prior to ticket issuance.

The Platform reserves the right to:
• Request additional verification.
• Suspend transactions pending review.
• Block Users suspected of fraudulent activity.

Payment providers may independently apply their own compliance measures.`,
  },
  {
    number: "8",
    title: "DELIVERY AND VALIDITY OF TICKETS",
    content: `Tickets are delivered electronically and are deemed delivered upon transmission.

Tickets must be presented in their original digital format and remain the responsibility of the User.

Any duplication, resale in violation of applicable law, or tampering may result in invalidation without compensation.`,
  },
  {
    number: "9",
    title: "REFUND, CANCELLATION AND EVENT POLICY",
    content: `Tickets are, as a general rule, non-refundable and non-exchangeable.

Where an event is canceled, postponed, or materially modified, responsibility for refunds lies exclusively with the event organizer, unless Afrodise Music SRL is explicitly identified as such.

Service fees and processing fees are non-refundable, as they represent services already rendered.`,
  },
  {
    number: "10",
    title: "USER CONDUCT",
    content: `Users agree to use the Platform lawfully and in good faith.

Any misuse, including fraudulent transactions, automated access, or disruption of services, may result in immediate termination and legal action.`,
  },
  {
    number: "11",
    title: "LIMITATION OF LIABILITY",
    content: `To the maximum extent permitted by law, the Platform is provided without warranties of any kind.

Afrodise Music SRL shall not be liable for indirect, incidental, or consequential damages, including loss of profit, data, or opportunity.

Where liability cannot be excluded, it shall be limited to the amount of fees paid by the User to the Platform.`,
  },
  {
    number: "12",
    title: "FORCE MAJEURE",
    content: `Neither Afrodise Music SRL nor Afrodise Music (UAE) shall be liable for failure or delay caused by events beyond reasonable control, including but not limited to natural disasters, pandemics, governmental restrictions, or technical failures.`,
  },
  {
    number: "13",
    title: "INTELLECTUAL PROPERTY",
    content: `All intellectual property rights associated with the Platform and brand are owned by Afrodise Music (UAE) or its licensors.

Unauthorized use is strictly prohibited.`,
  },
  {
    number: "14",
    title: "PRIVACY POLICY",
    content: `Afrodise Music SRL processes personal data in compliance with Regulation (EU) 2016/679 (GDPR).

Data is processed for contractual, legal, and legitimate business purposes, including payment processing, fraud prevention, and customer support.

Data may be shared with payment providers, event organizers, and service providers as necessary.

Users retain all rights provided under applicable data protection laws.`,
  },
  {
    number: "15",
    title: "COOKIE POLICY",
    content: `The Platform uses cookies to ensure proper functionality, enhance user experience, and support analytics and marketing.

Users may manage cookie preferences through browser settings.`,
  },
  {
    number: "16",
    title: "GOVERNING LAW AND JURISDICTION",
    content: `This Agreement shall be governed as follows:
• In matters relating to Platform operation, data protection, and payment processing, Romanian law and European Union regulations shall apply.
• In matters relating to event organization conducted by Afrodise Music (UAE), applicable United Arab Emirates law shall apply.`,
  },
  {
    number: "17",
    title: "DISPUTE RESOLUTION AND ARBITRATION",
    content: `Any dispute arising out of or in connection with this Agreement shall first be attempted to be resolved amicably.

If no resolution is reached, disputes shall be finally settled by arbitration in Dubai, United Arab Emirates, under applicable arbitration rules, conducted in English.

The arbitral decision shall be final and binding.

Nothing in this clause prevents Afrodise Music SRL from pursuing claims in competent courts where necessary for enforcement or recovery.`,
  },
  {
    number: "18",
    title: "MODIFICATIONS AND FINAL PROVISIONS",
    content: `Afrodise Music reserves the right to modify this Agreement at any time. Continued use of the Platform constitutes acceptance of such modifications.

If any provision is deemed invalid, the remaining provisions shall remain enforceable.

This Agreement constitutes the entire understanding between the parties.`,
  },
  {
    number: "19",
    title: "COMPANY INFORMATION",
    content: `Afrodise Music SRL
Bucharest, Romania
CIF: RO52584816

Afrodise Music (UAE)
CO-Working Facility FDCW1137
Compass Building, Al Shohada Road
Al Hamra Industrial Zone-FZ
Ras Al Khaimah, United Arab Emirates`,
  },
  {
    number: "20",
    title: "COPYRIGHT",
    content: `Copyright © 2026 Afrodise Music. All rights reserved.`,
  },
];

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 max-w-4xl mx-auto px-4 pt-24 pb-16 w-full">

        {/* HEADER */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight">
            Terms & Conditions, Privacy Policy, Cookie Policy and Refund Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3">
            Afrodise Music Platform · Last updated: 2026
          </p>
        </div>

        {/* DIVIDER */}
        <div className="w-full h-px bg-green-900/30 mb-10" />

        {/* SECTIONS */}
        <div className="space-y-10">
          {sections.map((section) => (
            <div key={section.number}>
              {/* SECTION TITLE */}
              <div className="flex items-start gap-4 mb-3">
                <span className="text-[10px] tracking-widest text-green-400/60 uppercase mt-1 flex-shrink-0">
                  {section.number}.
                </span>
                <h2 className="text-sm font-bold tracking-widest text-green-400/80 uppercase">
                  {section.title}
                </h2>
              </div>

              {/* SECTION CONTENT */}
              <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line pl-6">
                {section.content}
              </p>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
